import type { useSubscription } from '@apollo/client/react';
import { useQuery } from '@apollo/client/react';

import useAccount from '@zen/utils/hooks/useAccount';
import useResilientSubscription from '@zen/utils/hooks/useResilientSubscription';
import type { Undefinable } from '@zen/utils/typescript';

import {
  GetTaskListCountersDocument,
  type GetTaskListCountersQueryResult,
  type GetTaskListCountersQueryVariables,
  TaskDetailsCountersDocument,
  type TaskDetailsCountersSubscriptionResult,
  type TaskDetailsCountersSubscriptionVariables
} from '../graphql';
import type { SidebarCounters } from '../types';

type TaskListCount = GetTaskListCountersQueryResult['taskListCount'];
type TaskDetailsCounters = NonNullable<TaskDetailsCountersSubscriptionResult['taskDetails']>;

const variables: GetTaskListCountersQueryVariables = { taskDetailsFiltersInput: { onlyCurrentUsersTasks: true } };

const getCounters = (data: Undefinable<TaskListCount>): SidebarCounters => {
  return {
    taskListCount: data?.activeTaskCount || 0,
    allTaskListCount: data?.allOpenTaskCount || 0,
    unassignedTaskListCount: data?.allUnassignedTaskCount || 0
  };
};

const useAdminSidebarCounters = (): SidebarCounters => {
  const { userProfile } = useAccount();
  const skip: boolean = !userProfile.isAdmin;

  const { data, updateQuery } = useQuery<GetTaskListCountersQueryResult, GetTaskListCountersQueryVariables>(
    GetTaskListCountersDocument,
    { skip, variables }
  );

  const handleData = ({ data: subscriptionData }: useSubscription.OnDataOptions<TaskDetailsCountersSubscriptionResult>): void => {
    const taskDetails: Undefinable<TaskDetailsCounters> = subscriptionData.data?.taskDetails ?? undefined;

    if (!taskDetails) return;

    updateQuery((_unsafePreviousData, { complete, previousData }) => {
      if (!complete) return undefined;

      return { ...previousData, taskListCount: { ...previousData.taskListCount, ...taskDetails } };
    });
  };

  useResilientSubscription<TaskDetailsCountersSubscriptionResult, TaskDetailsCountersSubscriptionVariables>(
    TaskDetailsCountersDocument,
    {
      fetchPolicy: 'no-cache',
      ignoreResults: true,
      onData: handleData,
      skip,
      variables
    }
  );

  return getCounters(data?.taskListCount);
};

export default useAdminSidebarCounters;
